class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.searchRequestId = 0;
    this.resultsSearchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length ||
        (this.resultsSearchTerm === this.getQuery() && this.querySelector('[aria-selected="true"] a'))) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true' && this.resultsSearchTerm === currentSearchTerm) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    const position = currentButtonText.indexOf(previousTerm);
    if (position === -1 || currentButtonText.indexOf(previousTerm, position + previousTerm.length) !== -1) return;
    searchForTextElement.innerText = currentButtonText.slice(0, position) + newTerm +
      currentButtonText.slice(position + previousTerm.length);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    const activeElementIndex = selectedElementIndex === -1
      ? 0
      : (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) % allVisibleElements.length;

    if (this.statusElement) this.statusElement.textContent = '';
    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    if (this.resultsSearchTerm !== this.getQuery()) return;
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  invalidateSearchRequest() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.searchRequestId += 1;
  }

  isCurrentSearchRequest(searchTerm, requestId) {
    return this.isConnected && requestId === this.searchRequestId &&
      this.getQuery() === searchTerm && this.contains(document.activeElement);
  }

  disconnectedCallback() {
    this.invalidateSearchRequest();
  }

  getSearchResults(searchTerm) {
    this.invalidateSearchRequest();
    const requestId = this.searchRequestId;
    const queryKey = searchTerm;
    const signal = this.abortController.signal;
    if (!this.isCurrentSearchRequest(searchTerm, requestId)) return Promise.resolve();
    this.setLiveRegionLoadingState();
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return Promise.resolve();
    }

    return fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed (${response.status})`);
        return response.text();
      })
      .then((text) => {
        if (!this.isCurrentSearchRequest(searchTerm, requestId)) {
          searchDeferred?.reject(new DOMException('Search request is no longer current', 'AbortError'));
          return;
        }
        const section = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (!section?.querySelector('[data-predictive-search-live-region-count-value]')) {
          throw new Error('Predictive search response is missing results');
        }
        const resultsMarkup = section.innerHTML;
        document.querySelectorAll('predictive-search').forEach((predictiveSearchInstance) => {
          if (predictiveSearchInstance.cachedResults) predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup, searchTerm);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        searchDeferred?.reject(error);
        if (signal.aborted || !this.isCurrentSearchRequest(searchTerm, requestId)) return;
        this.removeAttribute('results');
        this.resultsSearchTerm = '';
        this.close();
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm = this.searchTerm) {
    this.resultsSearchTerm = searchTerm;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.invalidateSearchRequest();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
